import { api } from '../api';
import { provider, tags } from '../tags';
import type { BvAppealDoc, BvLog, DvAppealDoc, DvLog, operations } from '../types';

type CreateBvLogArgs = operations['createBvLog']['parameters']['path'];
type ListBvLogArgs = operations['listBvLogs']['parameters']['path'];
type CreateBvAppealDocArgs = operations['createBvAppealDoc']['parameters']['path'] & Omit<BvAppealDoc, 'id'>;
type ListBvAppealDocArgs = operations['listBvAppealDocs']['parameters']['path'];
type CreateDvLogArgs = operations['createDvLog']['parameters']['path'];
type ListDvLogArgs = operations['listDvLogs']['parameters']['path'];
type CreateDvAppealDocArgs = operations['createDvAppealDoc']['parameters']['path'] & Omit<DvAppealDoc, 'id'>;
type ListDvAppealDocArgs = operations['listDvAppealDocs']['parameters']['path'];

/* istanbul ignore file */

export const verificationsApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    /**
     * Request business verification
     */
    requestBv: builder.mutation<BvLog, CreateBvLogArgs>({
      query: ({ account_pk }) => ({
        url: `api/account/${account_pk}/bv/`,
        method: 'POST',
      }),
      invalidatesTags: [tags.account, tags.levelup],
    }),

    /**
     * Get all business verification requests
     */
    listBvLogs: builder.query<Array<BvLog>, ListBvLogArgs>({
      query: ({ account_pk }) => `api/account/${account_pk}/bv/`,
      providesTags: provider<BvLog>(tags.bvLog),
    }),

    /**
     * Appeal a rejected business verification
     */
    bvAppeal: builder.mutation<BvLog, CreateBvAppealDocArgs>({
      query: ({ account_pk, bv_pk, ...doc }) => ({
        url: `api/account/${account_pk}/bv/${bv_pk}/appeal/`,
        method: 'POST',
        body: doc,
      }),
      invalidatesTags: [tags.account, tags.bvAppeal, tags.levelup],
    }),

    /**
     * List docs previously attached to a bv appeal
     */
    listBvAppealDocs: builder.query<Array<BvAppealDoc>, ListBvAppealDocArgs>({
      query: ({ account_pk, bv_pk }) => ({
        url: `api/account/${account_pk}/bv/${bv_pk}/appeal/`,
      }),
      providesTags: [tags.bvAppeal],
    }),

    /**
     * Request domain verification
     */
    requestDv: builder.mutation<DvLog, CreateDvLogArgs>({
      query: ({ account_pk }) => ({
        url: `api/account/${account_pk}/dv/`,
        method: 'POST',
      }),
      invalidatesTags: [tags.account, tags.levelup],
    }),

    /**
     * Get all domain verification requests
     */
    listDvLogs: builder.query<Array<DvLog>, ListDvLogArgs>({
      query: ({ account_pk }) => `api/account/${account_pk}/dv/`,
      providesTags: provider<DvLog>(tags.dvLog),
    }),

    /**
     * Appeal a rejected domain verification
     */
    dvAppeal: builder.mutation<DvLog, CreateDvAppealDocArgs>({
      query: ({ account_pk, dv_pk, ...doc }) => ({
        url: `api/account/${account_pk}/dv/${dv_pk}/appeal/`,
        method: 'POST',
        body: doc,
      }),
      invalidatesTags: [tags.account, tags.dvAppeal, tags.levelup],
    }),

    /**
     * List docs previously attached to a dv appeal
     */
    listDvAppealDocs: builder.query<Array<DvAppealDoc>, ListDvAppealDocArgs>({
      query: ({ account_pk, dv_pk }) => ({
        url: `api/account/${account_pk}/dv/${dv_pk}/appeal/`,
      }),
      providesTags: [tags.dvAppeal],
    }),
  }),
});
